// src/promo/offer.js
//
// Single source of truth for the DYNAMIC, cyclical upgrade-ladder promo.
// Given the current user it resolves which offer to show:
//
//   Free       → 55% off SUPER     (first month)        — new subscription
//   Super      → 55% off CREATOR   (first month)        — upgrade (next cycle)
//   Creator    → 55% off PRO
//   Pro        → 55% off BUSINESS
//   Business   → 55% off ENTERPRISE
//   Enterprise → 55% off COINS top-up (top of ladder → pivot to coins)
//
// All ladder/tier metadata is reused from membershipTiers.js so we never
// duplicate names or prices. The pill, modal and wallet card all read the
// object this returns — change the campaign here, nowhere else.

import {
  MEMBERSHIP_PLAN_META,
  getRecommendedUpgrade,
  getMembershipPlanKeyFromUser,
} from "../Lexappstore/membershipTiers";

export const PROMO_PERCENT_OFF = 55;
export const MEMBERSHIP_PROMO_ID = "launch55";
export const COINS_PROMO_ID = "coins55";

// $8.55 / $17.55 / $19 — drop the decimals only when the value is whole.
function money(n) {
  const r = Math.round(Number(n || 0) * 100) / 100;
  return Number.isInteger(r) ? `$${r}` : `$${r.toFixed(2)}`;
}

// SUPER → Super (display-friendly)
function nice(name = "", key = "") {
  const s = String(name || key || "");
  return s ? s.charAt(0) + s.slice(1).toLowerCase() : s;
}

/**
 * Resolve the active offer for this user. Always returns a usable object
 * (never null) — the ladder bottoms out at a coins offer so there's always
 * something to show. The caller decides VISIBILITY (e.g. hide while a
 * promo discount is already active this cycle); this only decides WHAT.
 */
export function resolveOffer(user) {
  // Prefer the authoritative tier the backend mirrors onto the
  // subscription (classified from the Stripe price amount); fall back to
  // the best-effort localStorage-hint detection only when it's absent.
  const storedKey = String(user?.subscriptions?.vipPass?.planKey || "")
    .trim()
    .toLowerCase();
  const currentKey = storedKey || getMembershipPlanKeyFromUser(user);
  const targetKey = getRecommendedUpgrade(currentKey);

  // Top of the membership ladder → pivot to a coins recharge discount.
  if (!targetKey) {
    return {
      kind: "coins",
      promoId: COINS_PROMO_ID,
      currentKey,
      targetKey: null,
      percentOff: PROMO_PERCENT_OFF,
      pillLabel: `Get ${PROMO_PERCENT_OFF}% off Coins`,
      headline: `${PROMO_PERCENT_OFF}% OFF`,
      subhead: "Your next coins top-up",
      fineprint: `${PROMO_PERCENT_OFF}% off your next coins purchase. You'll also receive our newsletter.`,
      // Coins page handles its own discounted checkout (separate coupon).
      href: `/profile/coins?promo=${COINS_PROMO_ID}`,
    };
  }

  const meta = MEMBERSHIP_PLAN_META[targetKey] || {};
  const list = Number(meta.monthlyUsd || 0);
  const discounted = list * (1 - PROMO_PERCENT_OFF / 100);
  const name = nice(meta.name, targetKey);
  // Free user = brand-new subscription (Stripe Checkout, pays now at the
  // discounted price). Existing member = upgrade that applies next cycle.
  const mode = currentKey === "free" ? "new" : "upgrade";

  return {
    kind: "membership",
    promoId: MEMBERSHIP_PROMO_ID,
    currentKey,
    targetKey,
    targetName: name,
    mode,
    percentOff: PROMO_PERCENT_OFF,
    listPriceUsd: list,
    discountedPriceUsd: discounted,
    listPriceText: money(list),
    discountedPriceText: money(discounted),
    pillLabel: `Get ${PROMO_PERCENT_OFF}% off ${name}`,
    headline: `${PROMO_PERCENT_OFF}% OFF`,
    subhead:
      mode === "new"
        ? `Your first month of ${name}`
        : `Upgrade to ${name} — first month`,
    fineprint:
      mode === "new"
        ? `First month ${money(discounted)}, then ${money(list)}/mo — cancel anytime. You'll also receive our newsletter.`
        : `Next cycle ${money(discounted)}, then ${money(list)}/mo. You'll also receive our newsletter.`,
  };
}

/**
 * Is there a promo discount CURRENTLY active on this user's membership?
 * (Written onto subscriptions.vipPass.discount by the Stripe webhook.)
 * While true, the user just claimed → hide the pill until it expires next
 * cycle, at which point the ladder advances to the next rung.
 */
export function hasActivePromoDiscount(user) {
  const d = user?.subscriptions?.vipPass?.discount;
  if (!d) return false;
  const endsSec = Number(d.endsAt || d.appliesUntil || 0);
  if (!endsSec) return false;
  return endsSec * 1000 > Date.now();
}
